<!-- app-subdomain-creators\src\Components\NewsfeedCreatorMediaTab.vue -->

<template>
  <div class="lg:w-[1175px] mx-auto pb-10 pt-5 min-h-[650px]">
    <div class="w-full rounded-xl border border-zinc-200 bg-white shadow-sm">
      <!-- Header + tabs -->
      <div class="px-4 pt-3 mt-4 ">
        <h2 class="text-lg font-semibold text-zinc-900">
          Media
        </h2>

        <div class="mt-3 mb-1 flex gap-6 text-base px-2">
          <!-- Photos tab -->
          <button
            type="button"
            class="pb-2 relative"
            :class="activeTab === 'photos'
              ? 'text-[#2D8CFF] font-semibold'
              : 'text-zinc-500 hover:text-zinc-700'"
            @click="activeTab = 'photos'"
          >
            Photos
            <span
              v-if="activeTab === 'photos'"
              class="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#2D8CFF] rounded-full"
            ></span>
          </button>

          <!-- Videos tab -->
          <button
            type="button"
            class="pb-2 relative"
            :class="activeTab === 'videos'
              ? 'text-[#2D8CFF] font-semibold'
              : 'text-zinc-500 hover:text-zinc-700'"
            @click="activeTab = 'videos'"
          >
            Videos
            <span
              v-if="activeTab === 'videos'"
              class="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#2D8CFF] rounded-full"
            ></span>
          </button>
        </div>
      </div>

        <!-- Content -->
      <div class="p-4">
        <!-- Initial loading / error / empty -->
        <div
          v-if="isInitialLoad"
          class="py-8 text-center text-sm text-zinc-500"
        >
          Loading media…
        </div>

        <div
          v-else-if="errorMessage"
          class="py-8 text-center text-sm text-red-500"
        >
          {{ errorMessage }}
        </div>

        <div
          v-else-if="!currentGridItems.length"
          class="py-10 text-center text-sm text-zinc-500"
        >
          <span v-if="activeTab === 'photos'">
            No photos from posts yet.
          </span>
          <span v-else>
            No videos from posts yet.
          </span>
        </div>

        <!-- Media grid + infinite scroll sentinel -->
        <div v-else>
          <div class="grid grid-cols-5 gap-[6px]">
            <button
              v-for="item in currentGridItems"
              :key="item.key"
              type="button"
              class="relative w-full aspect-[4/3] overflow-hidden rounded-md bg-zinc-200 group"
              @click="openFromGrid(item)"
            >
              <!-- Photo -->
              <img
                v-if="item.type === 'image'"
                :src="item.thumbSrc"
                alt=""
                class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
              />

              <!-- Video -->
              <div
                v-else
                class="w-full h-full bg-black relative"
              >
                <img
                  v-if="item.thumbSrc"
                  :src="item.thumbSrc"
                  alt=""
                  class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-150"
                />
                <div
                  class="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    class="h-10 w-10 rounded-full bg-black/70 flex items-center justify-center"
                  >
                    <!-- simple play triangle -->
                    <div
                      class="ml-[2px] w-0 h-0 border-y-[8px] border-y-transparent border-l-[14px] border-l-white"
                    ></div>
                  </div>
                </div>
              </div>
            </button>
          </div>

          <!-- Loader / end-of-list / sentinel -->
          <div class="mt-4">
            <div
              v-if="loadingMore"
              class="py-2 text-center text-[12px] text-zinc-400"
            >
              Loading more…
            </div>

            <div
              v-else-if="!hasMore"
              class="py-2 text-center text-[11px] text-zinc-400"
            >
              You've reached the end.
            </div>

            <!-- IntersectionObserver target (only when there's more) -->
            <div
              v-if="hasMore"
              ref="mediaSentinel"
              class="h-[1px] w-full"
            ></div>
          </div>
        </div>
      </div>


    </div>

    <!-- Fullscreen Media Viewer -->
    <NewsFeedMediaPostViewer
      v-if="viewerPost"
      :show="viewerOpen"
      :media-items="viewerMediaItems"
      :initial-index="viewerInitialIndex"
      :post="viewerPost"
      :author-avatar-src="viewerAuthorAvatarSrc"
      :commenter-avatar-src="viewerCommenterAvatarSrc"
      :display-time="viewerDisplayTime"
      @close="viewerOpen = false"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineProps,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  defineExpose,
  nextTick,
} from 'vue';
import NewsFeedMediaPostViewer from '@/Components/NewsFeedMediaPostViewer.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  featureInstanceId: {
    // allow null at first; parent will fill it in
    default: null,
  },
  // so the viewer can say "Comment as X"
  currentUserName: {
    type: String,
    default: 'You',
  },
  currentUserAvatar: {
    type: String,
    default: null,
  },
});

const loading = ref(false);
const loadingMore = ref(false);   // specifically for "load more"
const errorMessage = ref('');
const posts = ref([]); // all published posts with attachments

const activeTab = ref('photos'); // 'photos' | 'videos'

// Pagination state
const PAGE_SIZE = 40;
const limit = ref(PAGE_SIZE);
const offset = ref(0);
const hasMore = ref(true);

// Initial load helper (for template "Loading media…" vs load-more)
const isInitialLoad = computed(
  () => loading.value && posts.value.length === 0
);

// Infinite scroll sentinel
const mediaSentinel = ref(null);
let mediaObserver = null;

const defaultAuthorAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

/* ─────────────────────────────────────────────
 * Fetch media for this instance
 * (now supports pagination via `append` flag)
 * ───────────────────────────────────────────*/
async function fetchMediaPosts(options = {}) {
  const append = !!options.append;

  console.log(
    '[MediaTab] fetchMediaPosts called, featureInstanceId =',
    props.featureInstanceId,
    'append =',
    append
  );

  if (!props.featureInstanceId) {
    console.log('[MediaTab] aborting fetch: no featureInstanceId yet');
    return;
  }

  if (loading.value || loadingMore.value) {
    // avoid overlapping requests
    return;
  }

  errorMessage.value = '';

  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
    posts.value = [];
    offset.value = 0;
    hasMore.value = true;
  }

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      limit: String(limit.value),
      offset: String(offset.value),
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-media?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || !data || data.success === false) {
      console.error('[MediaTab] fetch error:', res.status, data);
      errorMessage.value =
        (data && data.error) ||
        'There was a problem loading media from your posts.';
      if (!append) {
        posts.value = [];
        hasMore.value = false;
      }
      return;
    }

    const mediaRows = Array.isArray(data.media) ? data.media : [];

    const mappedPosts = mediaRows.map((row) => {
      const rawFileUrl = String(row.fileUrl || '').toLowerCase();
      const hasVideoExtension =
        rawFileUrl.includes('.m3u8') ||
        rawFileUrl.includes('.mp4') ||
        rawFileUrl.includes('.mov') ||
        rawFileUrl.includes('.m4v') ||
        rawFileUrl.includes('.webm') ||
        rawFileUrl.includes('.mkv') ||
        rawFileUrl.includes('.avi') ||
        rawFileUrl.includes('.ogv');
      const isVideo =
        (typeof row.mimeType === 'string' &&
          row.mimeType.toLowerCase().startsWith('video/')) ||
        String(row.fileType || '').toLowerCase() === 'video' ||
        String(row.hlsStatus || '').toLowerCase().trim() === 'ready' ||
        String(row.hlsStatus || '').toLowerCase().trim() === 'processing' ||
        String(row.hlsStatus || '').toLowerCase().trim() === 'queued' ||
        String(row.hlsPlaylistUrl || '').trim().length > 0 ||
        hasVideoExtension;

      const attachment = {
        fileId: row.fileId,
      };

      if (isVideo) {
        attachment.type = 'video';
        attachment.videoUrl = row.hlsPlaylistUrl || row.fileUrl;
        attachment.fileType = row.fileType || null;
        attachment.mimeType = row.mimeType || null;
        attachment.hlsPlaylistUrl = row.hlsPlaylistUrl || null;
        attachment.hlsStatus = row.hlsStatus || null;
        attachment.thumbnailUrl = row.fileId
          ? buildNewsfeedImageProxyUrl(row.fileId, { variant: 'thumbnail' })
          : null;
      } else {
        attachment.fileType = row.fileType || null;
        attachment.mimeType = row.mimeType || null;
        attachment.hlsPlaylistUrl = row.hlsPlaylistUrl || null;
        attachment.hlsStatus = row.hlsStatus || null;
        attachment.imageUrl = row.fileUrl;
        attachment.linkUrl = String(row.linkUrl || '').trim() || null;
        attachment.openInNewTab = row.openInNewTab !== false;
      }

      const body = row.content || '';

      const authorName =
        row.postedAsName ||
        props.currentUserName ||
        'You';

      const normalizedPostedAsAvatarUrl = normalizeNewsfeedImageUrlForInstance(
        row.postedAsAvatarUrl
      );
      const authorAvatar =
        normalizedPostedAsAvatarUrl ||
        props.currentUserAvatar ||
        defaultAuthorAvatar;

      return {
        id: row.postId,
        creatorId: row.creatorId,
        instanceId: row.instanceId,
        featureInstanceId: row.instanceId,
        body,
        content: body,
        authorName,
        postedAsName: row.postedAsName || null,
        authorAvatar,
        postedAsAvatarUrl: normalizedPostedAsAvatarUrl || null,
        createdAt: row.postCreatedAt || row.createdAt,
        attachments: [attachment],
      };
    });

    const filtered = mappedPosts.filter(
      (p) => Array.isArray(p.attachments) && p.attachments.length
    );

    if (append) {
      posts.value = posts.value.concat(filtered);
    } else {
      posts.value = filtered;
    }

    const pageCount = mediaRows.length;
    const backendTotal =
      typeof data.total === 'number' ? data.total : null;

    // hasMore logic: prefer explicit hasMore; fall back to total; then page size
    if (typeof data.hasMore === 'boolean') {
      hasMore.value = data.hasMore;
    } else if (backendTotal !== null) {
      hasMore.value = offset.value + pageCount < backendTotal;
    } else {
      hasMore.value = pageCount === limit.value;
    }

    offset.value += pageCount;

    console.log(
      '[MediaTab] page loaded: count=',
      pageCount,
      'offset now=',
      offset.value,
      'hasMore=',
      hasMore.value
    );
  } catch (err) {
    console.error('[MediaTab] network error:', err);
    errorMessage.value =
      'Network error while loading media. Please try again.';
    if (!append) {
      posts.value = [];
      hasMore.value = false;
    }
  } finally {
    if (append) {
      loadingMore.value = false;
    } else {
      loading.value = false;
    }
  }
}

function reloadMedia() {
  fetchMediaPosts({ append: false });
}

defineExpose({
  reloadMedia,
});

// 🔥 on mount: only fetch if we ALREADY have an ID
onMounted(() => {
  console.log(
    '[MediaTab] mounted, initial featureInstanceId =',
    props.featureInstanceId
  );
  if (props.featureInstanceId) {
    fetchMediaPosts({ append: false });
  }
});

// 🔥 when featureInstanceId changes from null → real ID, fetch
watch(
  () => props.featureInstanceId,
  (newVal, oldVal) => {
    console.log(
      '[MediaTab] featureInstanceId changed:',
      oldVal,
      '→',
      newVal
    );
    if (!newVal) return;
    fetchMediaPosts({ append: false });
  }
);

/* ─────────────────────────────────────────────
 * IntersectionObserver for infinite scroll
 * ───────────────────────────────────────────*/
async function setupMediaObserver() {
  if (mediaObserver) {
    mediaObserver.disconnect();
    mediaObserver = null;
  }

  const target = mediaSentinel.value;
  if (!target) return;

  mediaObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting) return;
      if (!hasMore.value) return;
      if (loading.value || loadingMore.value) return;

      console.log('[MediaTab] sentinel intersected → load more');
      loadMoreMedia();
    },
    {
      root: null, // viewport
      rootMargin: '0px 0px 200px 0px', // start a bit before bottom
      threshold: 0,
    }
  );

  mediaObserver.observe(target);
}

async function loadMoreMedia() {
  if (!hasMore.value) return;
  await fetchMediaPosts({ append: true });
}

onBeforeUnmount(() => {
  if (mediaObserver) {
    mediaObserver.disconnect();
    mediaObserver = null;
  }
});

/* ─────────────────────────────────────────────
 * Helpers to derive URLs + type from attachments
 * ───────────────────────────────────────────*/
function buildImageSrcFromAttachment(att) {
  // Primary: fileId → creator image endpoint
  if (att?.fileId) {
    return buildNewsfeedImageProxyUrl(att.fileId);
  }
  // Fallbacks from various shapes you've already used
  if (att?.thumbnailUrl) return att.thumbnailUrl;
  if (att?.posterUrl) return att.posterUrl;
  if (att?.secureImageUrl) return att.secureImageUrl;
  if (att?.imageUrl) return att.imageUrl;
  if (att?.url) return att.url;
  return null;
}

function buildVideoThumbFromAttachment(att) {
  // Prefer explicit thumbnail/poster first
  if (att?.thumbnailUrl) return att.thumbnailUrl;
  if (att?.fileId) return buildNewsfeedImageProxyUrl(att.fileId, { variant: 'thumbnail' });
  if (att?.posterUrl) return att.posterUrl;
  // Worst case, use the video URL itself as a still frame
  if (att?.secureVideoUrl) return att.secureVideoUrl;
  if (att?.videoUrl) return att.videoUrl;
  if (att?.url) return att.url;
  if (att?.fileId) return buildNewsfeedImageProxyUrl(att.fileId);
  return null;
}

function attachmentIsVideo(att) {
  if (att?.type === 'video') return true;
  if (att?.secureVideoUrl || att?.videoUrl) return true;
  if (String(att?.fileType || '').toLowerCase() === 'video') return true;
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const rawUrl = String(att?.url || att?.fileUrl || att?.file_url || '').toLowerCase();
  if (
    rawUrl.includes('.m3u8') ||
    rawUrl.includes('.mp4') ||
    rawUrl.includes('.mov') ||
    rawUrl.includes('.m4v') ||
    rawUrl.includes('.webm') ||
    rawUrl.includes('.mkv') ||
    rawUrl.includes('.avi') ||
    rawUrl.includes('.ogv')
  ) {
    return true;
  }
  // If your backend tags mime, you could extend here later
  return false;
}

function buildNewsfeedImageProxyUrl(fileId, options = {}) {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return null;

  const params = new URLSearchParams();
  const featureId = String(props.featureInstanceId || '').trim();
  if (featureId) params.set('featureInstanceId', featureId);
  const variant = String(options.variant || '').trim();
  if (variant) params.set('variant', variant);

  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${encodeURIComponent(normalizedFileId)}${qs ? `?${qs}` : ''}`;
}

function normalizeNewsfeedImageUrlForInstance(url) {
  const raw = String(url || '').trim();
  if (!raw) return null;
  const match = raw.match(/\/api\/image\/([a-f0-9-]{16,})/i);
  if (!match || !match[1]) return raw;
  return buildNewsfeedImageProxyUrl(match[1]);
}

/* ─────────────────────────────────────────────
 * Flatten all posts + attachments → grid items
 * Each item knows which post & which attachment index it came from.
 * ───────────────────────────────────────────*/
const photoItems = computed(() => {
  const out = [];

  for (const post of posts.value) {
    const atts = Array.isArray(post.attachments) ? post.attachments : [];
    atts.forEach((att, idx) => {
      if (attachmentIsVideo(att)) return;

      const src = buildImageSrcFromAttachment(att);
      if (!src) return;

      out.push({
        key: `${post.id}-img-${idx}-${src}`,
        type: 'image',
        thumbSrc: src,
        post,
        attachmentIndex: idx,
      });
    });
  }

  return out;
});

const videoItems = computed(() => {
  const out = [];

  for (const post of posts.value) {
    const atts = Array.isArray(post.attachments) ? post.attachments : [];
    atts.forEach((att, idx) => {
      if (!attachmentIsVideo(att)) return;

      const thumb = buildVideoThumbFromAttachment(att) || null;

      out.push({
        key: `${post.id}-vid-${idx}-${thumb}`,
        type: 'video',
        thumbSrc: thumb,
        post,
        attachmentIndex: idx,
      });
    });
  }

  return out;
});

const currentGridItems = computed(() =>
  activeTab.value === 'photos' ? photoItems.value : videoItems.value
);

// When grid items change, re-attach the observer to the sentinel
watch(
  currentGridItems,
  async (items) => {
    if (!items.length) return;
    await nextTick();
    setupMediaObserver();
  }
);

/* ─────────────────────────────────────────────
 * Viewer state (reuse same logic as Scheduled / Discussion)
 * ───────────────────────────────────────────*/
const viewerOpen = ref(false);
const viewerPost = ref(null);
const viewerInitialIndex = ref(0);

function openFromGrid(item) {
  if (!item || !item.post) return;

  const attachment = item.post?.attachments?.[item.attachmentIndex || 0];
  const destination = item.type === 'image' ? resolveSafeImageLink(attachment?.linkUrl) : null;
  if (destination) {
    if (attachment?.openInNewTab !== false) {
      window.open(destination, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(destination);
    }
    return;
  }

  const basePost = item.post || {};

  viewerPost.value = {
    ...basePost,
    // still defensively ensure authorName just in case
    authorName:
      basePost.authorName ||
      basePost.postedAsName ||
      basePost.currentUserName ||
      props.currentUserName ||
      'You',
  };

  viewerInitialIndex.value = item.attachmentIndex || 0;
  viewerOpen.value = true;
}

function resolveSafeImageLink(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch (_) {
    return null;
  }
}

// Build mediaItems for the currently viewed post
const viewerMediaItems = computed(() => {
  const post = viewerPost.value;
  if (!post) return [];

  const items = [];
  const atts = Array.isArray(post.attachments) ? post.attachments : [];

  if (atts.length) {
    for (const att of atts) {
      if (attachmentIsVideo(att)) {
        const src =
          att.videoUrl || att.secureVideoUrl || att.url || null;
        if (src) {
          items.push({
            type: 'video',
            fileId: att.fileId ? String(att.fileId) : null,
            src,
            poster: att.posterUrl || att.thumbnailUrl || null,
          });
        }
        continue;
      }

      const imgSrc = buildImageSrcFromAttachment(att);
      if (imgSrc) {
        items.push({ type: 'image', src: imgSrc });
      }
    }
  }

  return items;
});

const viewerAuthorAvatarSrc = computed(() => {
  const post = viewerPost.value;
  if (!post) return defaultAuthorAvatar;

  return (
    post.postedAsAvatarUrl ||
    post.authorAvatar ||
    defaultAuthorAvatar
  );
});

const viewerCommenterAvatarSrc = computed(() => {
  return props.currentUserAvatar || viewerAuthorAvatarSrc.value;
});

/* ─────────────────────────────────────────────
 * Display time for viewer sidebar (similar style to other posts)
 * ───────────────────────────────────────────*/
function formatTimeParts(d) {
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return { hour12: hours, minutes, ampm };
}

function formatShortDate(d) {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const { hour12, minutes, ampm } = formatTimeParts(d);
  return `${month} ${day} at ${hour12}:${minutes} ${ampm}`;
}

const viewerDisplayTime = computed(() => {
  const post = viewerPost.value;
  if (!post || !post.createdAt) return '';

  // createdAt is a timestamp from MySQL → let Date parse it
  const d = new Date(post.createdAt);
  if (Number.isNaN(d.getTime())) return '';
  return formatShortDate(d);
});
</script>


